// 📄 registroHorasService.ts
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { getTarifa } from './tarifaService.ts';

export type EstadoFacturacion = 'Pendiente' | 'Facturado';
export const ESTADOS_FACTURACION: EstadoFacturacion[] = ['Pendiente', 'Facturado'];

export type EstadoAprobacion = 'Pendiente' | 'Aceptado' | 'Objetado';
export const ESTADOS_APROBACION: EstadoAprobacion[] = ['Pendiente', 'Aceptado', 'Objetado'];

export interface RegistroHorasFormData {
  fecha: string; // yyyy-mm-dd (input type="date"); se guarda como Timestamp
  solucionRelacionada: string;
  horas: number;
  huboSesion: boolean;
  asistentes: string;
  cambiosRealizados: string;
  conclusiones: string;
}

export interface RegistroHoras {
  id: string;
  fecha: any;
  solucionRelacionada: string;
  horas: number;
  huboSesion: boolean;
  asistentes: string;
  cambiosRealizados: string;
  conclusiones: string;
  tarifaHora: number;
  totalCalculado: number;
  estadoFacturacion: EstadoFacturacion;
  estadoAprobacion: EstadoAprobacion;
  comentarioCliente: string;
  fechaRevisionCliente?: any;
  creadoPor: string;
  fechaCreacion?: any;
}

const registrosRef = (projectId: string) => collection(db, "projects", projectId, "registrosHoras");
const registroRef = (projectId: string, registroId: string) => doc(db, "projects", projectId, "registrosHoras", registroId);

const validarFormulario = (data: RegistroHorasFormData) => {
  if (!data.fecha) throw new Error("La fecha es obligatoria.");
  // La solución/área la exige el formulario solo cuando el proyecto tiene
  // (proyectos generales sin áreas registran sin ella).
  if (!data.horas || data.horas <= 0) throw new Error("Las horas deben ser mayores a 0.");
};

const redondear = (n: number) => Math.round(n * 100) / 100;

export const getMisRegistros = async (projectId: string, uid: string): Promise<RegistroHoras[]> => {
  const q = query(registrosRef(projectId), where("creadoPor", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => {
      const data = d.data() as Omit<RegistroHoras, 'id'>;
      return {
        id: d.id,
        ...data,
        // Registros creados antes de esta funcionalidad no tienen estos
        // campos — se tratan como "todavía sin revisar por el cliente".
        estadoAprobacion: data.estadoAprobacion || 'Pendiente',
        comentarioCliente: data.comentarioCliente || ''
      };
    })
    .sort((a, b) => (b.fecha?.toMillis?.() ?? 0) - (a.fecha?.toMillis?.() ?? 0));
};

/**
 * Copia la tarifa por hora vigente del usuario en ese proyecto al momento
 * de crear el registro, así si luego la cambia, los registros viejos no
 * se alteran retroactivamente.
 */
export const createRegistro = async (
  projectId: string,
  uid: string,
  data: RegistroHorasFormData
): Promise<string> => {
  validarFormulario(data);

  const tarifaHora = (await getTarifa(projectId, uid)) ?? 0;
  const totalCalculado = redondear(data.horas * tarifaHora);

  const ref = doc(registrosRef(projectId));
  await setDoc(ref, {
    fecha: Timestamp.fromDate(new Date(`${data.fecha}T00:00:00`)),
    solucionRelacionada: data.solucionRelacionada,
    horas: data.horas,
    huboSesion: data.huboSesion,
    asistentes: data.asistentes.trim(),
    cambiosRealizados: data.cambiosRealizados.trim(),
    conclusiones: data.conclusiones.trim(),
    tarifaHora,
    totalCalculado,
    estadoFacturacion: 'Pendiente' as EstadoFacturacion,
    estadoAprobacion: 'Pendiente' as EstadoAprobacion,
    comentarioCliente: '',
    creadoPor: uid,
    fechaCreacion: serverTimestamp()
  });
  return ref.id;
};

/**
 * El total se recalcula con la tarifa que ya quedó fijada en el registro
 * (no con la tarifa actual del usuario, que pudo haber cambiado desde
 * entonces). Solo se puede editar mientras siga "Pendiente".
 */
export const updateRegistro = async (
  projectId: string,
  registroId: string,
  data: RegistroHorasFormData
) => {
  validarFormulario(data);

  const ref = registroRef(projectId, registroId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El registro no existe.");
  const actual = snap.data() as RegistroHoras;
  if (actual.estadoFacturacion !== 'Pendiente') {
    throw new Error("Este registro ya fue facturado; reábrelo antes de editarlo.");
  }
  if (actual.estadoAprobacion === 'Aceptado') {
    throw new Error("Este registro ya fue aceptado por el cliente; reábrelo antes de editarlo.");
  }

  const updates: Record<string, unknown> = {
    fecha: Timestamp.fromDate(new Date(`${data.fecha}T00:00:00`)),
    solucionRelacionada: data.solucionRelacionada,
    horas: data.horas,
    huboSesion: data.huboSesion,
    asistentes: data.asistentes.trim(),
    cambiosRealizados: data.cambiosRealizados.trim(),
    conclusiones: data.conclusiones.trim(),
    totalCalculado: redondear(data.horas * actual.tarifaHora)
  };

  // Corregir un registro objetado lo reenvía a revisión: vuelve a
  // "Pendiente" y borra el comentario viejo (sin guardar historial, misma
  // convención de escritura mínima que usa el resto del proyecto para
  // transiciones de estado — ver marcarFacturado/reabrirRegistro).
  if (actual.estadoAprobacion === 'Objetado') {
    updates.estadoAprobacion = 'Pendiente' as EstadoAprobacion;
    updates.comentarioCliente = '';
  }

  await updateDoc(ref, updates);
};

export const deleteRegistro = async (projectId: string, registroId: string) => {
  const ref = registroRef(projectId, registroId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const actual = snap.data() as RegistroHoras;
  if (actual.estadoFacturacion !== 'Pendiente') {
    throw new Error("Este registro ya fue facturado; reábrelo antes de eliminarlo.");
  }
  if (actual.estadoAprobacion === 'Aceptado') {
    throw new Error("Este registro ya fue aceptado por el cliente; reábrelo antes de eliminarlo.");
  }
  await deleteDoc(ref);
};

export const marcarFacturado = async (projectId: string, registroId: string) => {
  await updateDoc(registroRef(projectId, registroId), { estadoFacturacion: 'Facturado' as EstadoFacturacion });
};

export const reabrirRegistro = async (projectId: string, registroId: string) => {
  await updateDoc(registroRef(projectId, registroId), { estadoFacturacion: 'Pendiente' as EstadoFacturacion });
};

/**
 * El consultor "reabre" una aprobación para poder corregir el registro.
 * Solo puede volverla a "Pendiente" — nunca aprobar/objetar en nombre del
 * cliente (eso lo bloquean las reglas de Firestore, ver aprobarRegistro
 * / objetarRegistro más abajo, que son las únicas que pueden escribir
 * "Aceptado"/"Objetado").
 */
export const reabrirAprobacion = async (projectId: string, registroId: string) => {
  await updateDoc(registroRef(projectId, registroId), { estadoAprobacion: 'Pendiente' as EstadoAprobacion });
};

/**
 * Las siguientes dos las usa el rol Cliente para decidir sobre un
 * registro ajeno (nunca es dueño de ninguno). Van directo a Firestore
 * (no por onehubApi) porque las reglas ya acotan el `update` a estos 3
 * campos exactos — no hace falta un endpoint nuevo solo para esto, y así
 * no se pierde el resto de la info sensible que sí sigue oculta por el
 * resumen server-side (ver resumenHorasService.ts).
 */
export const aprobarRegistro = async (projectId: string, registroId: string) => {
  await updateDoc(registroRef(projectId, registroId), {
    estadoAprobacion: 'Aceptado' as EstadoAprobacion,
    comentarioCliente: '',
    fechaRevisionCliente: serverTimestamp()
  });
};

export const objetarRegistro = async (projectId: string, registroId: string, comentario: string) => {
  const texto = comentario.trim();
  if (!texto) throw new Error("Escribe un comentario explicando la objeción.");
  await updateDoc(registroRef(projectId, registroId), {
    estadoAprobacion: 'Objetado' as EstadoAprobacion,
    comentarioCliente: texto,
    fechaRevisionCliente: serverTimestamp()
  });
};
